import axios, { AxiosInstance, AxiosError, AxiosResponse } from 'axios';
import type { ApiError } from '../types';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 5000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Track whether backend is available
let backendAvailable: boolean | null = null;

/**
 * Check if backend is available
 */
export async function isBackendAvailable(): Promise<boolean> {
  if (backendAvailable !== null) {
    return backendAvailable;
  }

  try {
    await axios.get(`${API_BASE_URL}/health`, { timeout: 3000 });
    backendAvailable = true;
    return true;
  } catch {
    backendAvailable = false;
    return false;
  }
}

/**
 * Reset backend availability check (useful for retrying)
 */
export function resetBackendCheck(): void {
  backendAvailable = null;
}

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError<ApiError>) => {
    // Mark backend as unavailable on connection errors
    if (!error.response) {
      backendAvailable = false;
    }

    if (error.response) {
      const { status, data } = error.response;

      // Rate limiting
      if (status === 429) {
        return Promise.reject({
          status: 429,
          title: 'Too Many Requests',
          detail: 'Too many requests. Please wait before trying again.',
          errors: [],
        } as ApiError);
      }

      // Validation errors
      if (status === 400 && data) {
        return Promise.reject(data);
      }

      // Server errors
      if (status >= 500) {
        return Promise.reject({
          status,
          title: 'Server Error',
          detail: 'An unexpected error occurred. Please try again later.',
          errors: [],
        } as ApiError);
      }

      return Promise.reject(data);
    }

    // Network errors - this is normal when backend is not available
    return Promise.reject({
      status: 0,
      title: 'Backend Unavailable',
      detail: 'Using local data. Backend service is not available.',
      errors: [],
    } as ApiError);
  }
);

export default apiClient;
